import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'selectors';
import { useNotification } from 'hooks/useNotification';
import { teamQueryKeys } from 'TeamModule/query/teamQueryKeys';
import {
  enableDirectDatabaseIntegrationApi,
  EnableDirectDatabaseIntegrationPayload,
} from 'TeamModule/api/enableDirectDatabaseIntegrationApi';

// Shared key so every hook instance (About-card button, Actions-menu item)
// observes the same in-flight state via useIsMutating. Deliberately NOT
// scoped per team: enabling is a rare, seconds-long operation, and briefly
// disabling the control on other teams' pages is the safer trade-off vs.
// threading teamId through both call sites.
const ENABLE_DDI_MUTATION_KEY = ['enableDirectDatabaseIntegration'];

export const useEnableDirectDatabaseIntegrationMutation = () => {
  const token = useSelector(selectAuthToken) as string | undefined;
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const mutation = useMutation<unknown, unknown, EnableDirectDatabaseIntegrationPayload>({
    mutationKey: ENABLE_DDI_MUTATION_KEY,
    mutationFn: async (params) => {
      if (!token) throw new Error('Not Authenticated');
      return enableDirectDatabaseIntegrationApi({ params, token });
    },
    onSuccess: () => {
      showSuccess('Direct Database Integration enabled successfully');
      // Returning the promise keeps the mutation pending until active team
      // queries have refetched, so the Enable controls stay disabled through
      // the window where ddiEnabled would still read false.
      return queryClient.invalidateQueries({
        queryKey: teamQueryKeys.lists(),
      });
    },
    onError: () => {
      showError('Failed to enable Direct Database Integration');
    },
  });

  // Counts in-flight enable mutations across ALL hook instances, unlike
  // mutation.isPending which is local to this instance.
  const enablingCount = useIsMutating({ mutationKey: ENABLE_DDI_MUTATION_KEY });

  return {
    enableDirectDatabaseIntegration: mutation.mutate,
    isEnablingDirectDatabaseIntegration: enablingCount > 0,
  };
};

export default useEnableDirectDatabaseIntegrationMutation;
